import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Swal from "sweetalert2";
import i18n from "i18next";
import pako from "pako";

let socketInstance = null;
const listeners = [];

// 🔥 NEW: Enhanced listener management for message filtering
const messageTypeListeners = new Map(); // Specific message type listeners
const messageTypeExclusions = new Map(); // Message type exclusions per listener

let shouldReconnect = true;
const messageQueue = [];
let reconnectSwalVisible = false;

const showOfflineSwal = (wsUrl, currentUser) => {
  if (reconnectSwalVisible) return;

  reconnectSwalVisible = true;
  Swal.fire({
    title: i18n.t("You are offline"),
    text: i18n.t("Press below to try reconnecting"),
    icon: "warning",
    confirmButtonText: i18n.t("Reconnect"),
    allowOutsideClick: false,
    allowEscapeKey: false,
    backdrop: true,
    didClose: () => {
      reconnectSwalVisible = false;
    },
  }).then((result) => {
    if (result.isConfirmed) {
      resetWebSocket(wsUrl, currentUser);
    }
  });
};

export const setupWebSocket = (wsUrl, currentUser) => {
  if (
    socketInstance &&
    (socketInstance.readyState === WebSocket.CONNECTING ||
      socketInstance.readyState === WebSocket.OPEN)
  ) {
    //console.log("WebSocket is already initialized.");
    return socketInstance;
  }

  shouldReconnect = true;
  socketInstance = new WebSocket(wsUrl);
  socketInstance.binaryType = "arraybuffer"; // Necessary for binary send

  socketInstance.onopen = () => {
    if (currentUser) {
      const loginMessage = { ...generalMessageTemplate };
      loginMessage.messageType = "user_login_social";
      loginMessage.browserId = localStorage.getItem("browserId");
      loginMessage.user.socialUser = currentUser;
      sendCompressedMessage(loginMessage);
    }

    // Resend queued messages
    while (messageQueue.length > 0) {
      const queuedMsg = messageQueue.shift();
      sendCompressedMessage(queuedMsg);
    }

    notifyListeners({ type: "status", status: "open" });
    if (reconnectSwalVisible) {
      Swal.close();
      reconnectSwalVisible = false;
    }
  };

  socketInstance.onmessage = (event) => {
    try {
      const message = JSON.parse(typeof event.data === "string" ? event.data : new TextDecoder().decode(event.data));
      notifyListeners({ type: "message", message });

      if (message.messageType === "user_logged_out") {
        window.location.href = "https://landing.democraticchess.com/";
      }
    } catch (e) {
      console.error("Invalid incoming WebSocket message", e);
    }
  };

  socketInstance.onclose = () => {
    //console.log("WebSocket closed.");
    notifyListeners({ type: "status", status: "closed" });

    if (shouldReconnect) {
      showOfflineSwal(wsUrl, currentUser);
      setTimeout(() => {
        setupWebSocket(wsUrl, currentUser);
      }, 5000);
    }
  };

  socketInstance.onerror = (error) => {
    //console.error("WebSocket error:", error);
    notifyListeners({ type: "status", status: "error" });
  };

  return socketInstance;
};

export const resetWebSocket = (wsUrl, currentUser) => {
  if (socketInstance) {
    shouldReconnect = false;
    socketInstance.onclose = null;
    socketInstance.close();
    socketInstance = null;
  }
  shouldReconnect = true;
  setupWebSocket(wsUrl, currentUser);
};

export const getWebSocketInstance = () => socketInstance;

// 🔥 EXISTING: General listener management (unchanged)
export const addListener = (listener) => {
  if (typeof listener === "function") {
    listeners.push(listener);
  }
};

export const removeListener = (listener) => {
  const index = listeners.indexOf(listener);
  if (index > -1) {
    listeners.splice(index, 1);
  }
};

// 🔥 NEW: Add listener for specific message types only
export const addMessageTypeListener = (messageTypes, listener) => {
  const types = Array.isArray(messageTypes) ? messageTypes : [messageTypes];
  //console.log(`🎯 Adding specific listener for message types:`, types);
  
  types.forEach(messageType => {
    if (!messageTypeListeners.has(messageType)) {
      messageTypeListeners.set(messageType, new Set());
    }
    messageTypeListeners.get(messageType).add(listener);
  });
};

// 🔥 NEW: Remove listener for specific message types
export const removeMessageTypeListener = (messageTypes, listener) => {
  const types = Array.isArray(messageTypes) ? messageTypes : [messageTypes];
  //console.log(`🧹 Removing specific listener for message types:`, types);
  
  types.forEach(messageType => {
    if (messageTypeListeners.has(messageType)) {
      messageTypeListeners.get(messageType).delete(listener);
      if (messageTypeListeners.get(messageType).size === 0) {
        messageTypeListeners.delete(messageType);
      }
    }
  });
};

// 🔥 NEW: Add exclusion - listener will NOT receive these message types
export const addMessageTypeExclusion = (messageTypes, listener) => {
  const types = Array.isArray(messageTypes) ? messageTypes : [messageTypes];
  //console.log(`🚫 Adding message type exclusions:`, types, `for listener`);
  
  if (!messageTypeExclusions.has(listener)) {
    messageTypeExclusions.set(listener, new Set());
  }
  types.forEach(messageType => {
    messageTypeExclusions.get(listener).add(messageType);
  });
};

// 🔥 NEW: Remove exclusion
export const removeMessageTypeExclusion = (messageTypes, listener) => {
  if (!messageTypeExclusions.has(listener)) return;
  
  const types = Array.isArray(messageTypes) ? messageTypes : [messageTypes];
  //console.log(`✅ Removing message type exclusions:`, types, `for listener`);
  
  types.forEach(messageType => {
    messageTypeExclusions.get(listener).delete(messageType);
  });
  
  if (messageTypeExclusions.get(listener).size === 0) {
    messageTypeExclusions.delete(listener);
  }
};

// 🔥 ENHANCED: Smart message routing with filtering
const notifyListeners = (event) => {
  const messageType = event.message?.messageType;
  
  if (event.type === "message" && messageType) {
    //console.log(`📨 WebSocket Manager routing message: ${messageType}`);
    
    // First, send to specific message type listeners
    const specificListeners = messageTypeListeners.get(messageType);
    if (specificListeners && specificListeners.size > 0) {
      //console.log(`🎯 Sending ${messageType} to ${specificListeners.size} specific listeners`);
      specificListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          //console.error(`Error in specific listener for ${messageType}:`, error);
        }
      });
      
      // If we have specific listeners, don't send to general listeners
      // This prevents duplicate handling
      return;
    }
    
    // Send to general listeners (but respect exclusions)
    let generalListenerCount = 0;
    listeners.forEach(listener => {
      // Check if this listener has exclusions for this message type
      const exclusions = messageTypeExclusions.get(listener);
      if (exclusions && exclusions.has(messageType)) {
        //console.log(`🚫 Excluding ${messageType} from general listener (excluded)`);
        return;
      }
      
      try {
        listener(event);
        generalListenerCount++;
      } catch (error) {
        //console.error(`Error in general listener for ${messageType}:`, error);
      }
    });
    
    if (generalListenerCount > 0) {
      //console.log(`📢 Sent ${messageType} to ${generalListenerCount} general listeners`);
    }
  } else {
    // For non-message events (status changes), send to all general listeners
    //console.log(`📢 Broadcasting ${event.type} to ${listeners.length} general listeners`);
    listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        //console.error(`Error in listener for ${event.type}:`, error);
      }
    });
  }
};

const sendCompressedMessage = (message) => {
  try {
    const browserId = localStorage.getItem("browserId");
    message.browserId = browserId;

    const json = JSON.stringify(message);
    const compressed = pako.gzip(json);

    if (
      socketInstance &&
      socketInstance.readyState === WebSocket.OPEN
    ) {
      socketInstance.send(compressed);
    } else {
      console.warn("WebSocket not open. Queuing compressed message...");
      messageQueue.push(message); // still store original (uncompressed) for retry
    }
  } catch (e) {
    console.error("Failed to compress WebSocket message", e);
  }
};

export const getSocket = () => socketInstance;

// EXPORTED API
export const sendMessage = sendCompressedMessage; // Main export for app use